<template>
  <article v-if="exhibition && Object.keys(exhibition).length">
    <MediaBannerHandler
      v-if="exhibition.mediaBanner && exhibition.mediaBanner.enabled"
      :media-banner="exhibition.mediaBanner"
      :is-header-banner="true"
    ></MediaBannerHandler>
    <PostFeaturedImage
      v-else-if="exhibition.featuredImage"
      :featured-image="exhibition.featuredImage.node"
      :featured-image-full="exhibition.featuredImageFull"
      :ga4-event="
        JSON.stringify({
          click_type: 'hero',
          component: 'ExhibitionsPost',
          content_group: 'exhibition',
          gtm_tag: 'image',
          file_name: `${exhibition.featuredImage.node.sourceUrl.split('/').pop()}`,
        })
      "
      :is-wide="exhibition.featuredImageWidth === 'wide'"
      :srcset="exhibition.imageSrcset"
    ></PostFeaturedImage>
    <section :class="$style.grid">
      <header>
        <nav>
          <BackLink
            v-if="exhibition.dates"
            component="ExhibitionsPost"
            group="exhibition"
            :label="exhibition.dates.status !== 'past' ? 'Exhibitions' : 'Past Exhibitions'"
            :name="exhibition.dates.status !== 'past' ? 'ExhibitionsPage' : 'ExhibitionsPast'"
            :params="{ past: exhibition.dates.status === 'past' }"
          ></BackLink>
        </nav>
        <div :class="$style.content">
          <h1 v-html="exhibition.title"></h1>
          <span v-if="exhibition.dates.label" :class="$style.subtitle">
            {{ exhibition.dates.label }}
          </span>
          <ExhibitionDate
            v-else
            :begin="exhibition.dates.start"
            :class="$style.subtitle"
            :end="exhibition.dates.end"
            :status="exhibition.dates.status"
          ></ExhibitionDate>
          <a
            v-if="buyTickets(exhibition)"
            :class="[$style.buy, 'button-primary']"
            :href="exhibition.ticketLink"
            :ga4-event="
              JSON.stringify({
                click_type: 'cta',
                component: 'ExhibitionsPost',
                content_group: 'exhibition',
                gtm_tag: 'link',
                link_text: 'Buy Tickets',
                link_url: `${exhibition.ticketLink}`,
              })
            "
            target="_blank"
          >
            Buy Tickets
          </a>
        </div>
      </header>
      <ProcessContent
        v-if="exhibition && exhibition.content"
        :class="$style.content"
        :content="exhibition.content"
        wrapper="section"
      ></ProcessContent>
    </section>
  </article>
</template>

<script setup lang="ts">
import BackLink from '@/components/common/BackLink.vue';
import { BootStrap } from '@/types/global.interface';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import { Exhibition } from '@/types/exhibition.interface';
import ExhibitionDate from '@/components/exhibitions/ExhibitionDate.vue';
import { exhibitionQuery } from '@/queries/ExhibitionQuery';
import { inject, ref } from 'vue';
import MediaBannerHandler from '@/components/mediaBanner/MediaBannerHandler.vue';
import PostFeaturedImage from '@/components/common/PostFeaturedImage.vue';
import ProcessContent from '@/components/common/ProcessContent.vue';
import { storeToRefs } from 'pinia';
import { useMainStore } from '@/stores/mainStore.js';
import { useQuery } from 'villus';

const props = defineProps<{
  slug: string;
}>();

const bootstrap = inject<BootStrap>('bootstrap');

const store = useMainStore();
const { useBootstrap } = storeToRefs(store);
const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();

const exhibition = ref<Exhibition>({});
const shouldSkip = ref(true);

const { data } = useQuery({
  onData: (data) => {
    if (data.exhibition && Object.keys(data.exhibition).length) {
      exhibition.value = data.exhibition;
      setMetaTitle(data.exhibition.title);
      setMetaDescription(data.exhibition.excerpt);
      dataLoaded();
    }

    if (!data.exhibition || Object.keys(data.exhibition).length === 0) {
      handleError('ExhibitionsPost', props.slug);
    }
  },
  query: exhibitionQuery,
  skip: shouldSkip,
  variables: { slug: props.slug },
});

if (useBootstrap.value) {
  shouldSkip.value = true;

  if (bootstrap.initial.queried_object) {
    exhibition.value = bootstrap.initial.queried_object.pagedata;
    setMetaTitle(exhibition.value.title);
    setMetaDescription(exhibition.value.excerpt);
    dataLoaded();
  }

  if (!bootstrap.initial.queried_object) {
    shouldSkip.value = false;
  }
}

if (!useBootstrap.value) {
  shouldSkip.value = false;
}

function buyTickets(post): boolean {
  return post.ticketLink && post.dates.status !== 'past';
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: exhibitionQuery,
          variables: { slug: params.slug },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss" module>
.buy {
  display: block;
  margin-top: 1rem;
  width: fit-content;
}

.subtitle {
  display: block;
  margin-top: 0;
}

.title {
  margin-top: 4rem;
}

@media (min-width: $break-point) {
  .content {
    @include grid-content;
  }
}
</style>
